import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";

export function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium ${
        status === "published"
          ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
          : "border-amber-400/20 bg-amber-400/10 text-amber-300"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${status === "published" ? "bg-emerald-300" : "bg-amber-300"}`} />
      {status === "published" ? "Published" : "Draft"}
    </span>
  );
}

export function ConfirmModal({ target, onCancel, onConfirm, noun }) {
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="delete-confirm-modal">
      <div className="glass-strong w-full max-w-sm rounded-3xl p-7">
        <h3 className="font-display text-lg font-bold text-white">Delete {noun}?</h3>
        <p className="mt-2 text-sm text-neutral-400">
          “{target.title}” will be permanently removed. This cannot be undone.
        </p>
        <div className="mt-6 flex gap-3">
          <button onClick={onCancel} className="btn-ghost flex-1 !py-2.5 text-xs" data-testid="delete-cancel-button">
            <X className="h-4 w-4" /> Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500/90 px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-red-500"
            data-testid="delete-confirm-button"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("custom");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/portfolio").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load projects")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    let out = items.filter(
      (p) =>
        (cat === "All" || p.category === cat || p.categories?.includes(cat)) &&
        (status === "all" || p.status === status) &&
        (p.title.toLowerCase().includes(q.toLowerCase()) || (p.client || "").toLowerCase().includes(q.toLowerCase()))
    );
    out.sort((a, b) => {
      if (sort === "custom") return (a.order ?? 0) - (b.order ?? 0);
      if (sort === "title") return a.title.localeCompare(b.title);
      const da = a.created_at || "";
      const dbt = b.created_at || "";
      return sort === "oldest" ? da.localeCompare(dbt) : dbt.localeCompare(da);
    });
    return out;
  }, [items, q, cat, status, sort]);

  const canReorder = sort === "custom" && !q && cat === "All" && status === "all";

  const onDropRow = async (targetId) => {
    if (!dragId || dragId === targetId || !canReorder) {
      setDragId(null);
      setDropTargetId(null);
      return;
    }
    const ids = filtered.map((x) => x.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setItems(ids.map((id, idx) => ({ ...items.find((x) => x.id === id), order: idx })));
    setDragId(null);
    setDropTargetId(null);
    setSavingOrder(true);
    try {
      await api.post("/admin/portfolio/reorder", { ids });
      toast.success("Project order saved");
    } catch {
      toast.error("Could not save order");
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/portfolio/${p.id}/status`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: next } : x)));
      toast.success(next === "published" ? "Project published" : "Project unpublished");
    } catch {
      toast.error("Could not update status");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/portfolio/${deleteTarget.id}`);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Project deleted");
    } catch {
      toast.error("Could not delete project");
    }
    setDeleteTarget(null);
  };

  return (
    <div data-testid="admin-portfolio-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Portfolio</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {items.length} projects total
            {savingOrder ? " — saving order…" : canReorder ? " — drag rows to set the order shown on your site" : ""}
          </p>
        </div>
        <Link to="/admin/portfolio/new" className="btn-primary !px-5 !py-2.5 text-xs" data-testid="new-project-button">
          <Plus className="h-4 w-4" /> New Project
        </Link>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search projects…"
          className="input-glass !w-auto min-w-[200px] flex-1 !py-2.5"
          data-testid="portfolio-search-input"
        />
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="input-glass !w-auto !py-2.5" data-testid="portfolio-category-filter">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-glass !w-auto !py-2.5" data-testid="portfolio-status-filter">
          <option value="all">All status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="input-glass !w-auto !py-2.5" data-testid="portfolio-sort-select">
          <option value="custom">Custom order</option>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="title">Title A–Z</option>
        </select>
      </div>

      <div className="glass overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="portfolio-table">
          <thead>
            <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="w-10 px-3 py-4"></th>
              <th className="px-5 py-4">Project</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500" data-testid="portfolio-empty">No projects match your filters.</td></tr>
            )}
            {filtered.map((p) => (
              <tr
                key={p.id}
                draggable={canReorder}
                onDragStart={() => setDragId(p.id)}
                onDragOver={(e) => {
                  if (canReorder && dragId) {
                    e.preventDefault();
                    setDropTargetId(p.id);
                  }
                }}
                onDragLeave={() => setDropTargetId((cur) => (cur === p.id ? null : cur))}
                onDrop={() => onDropRow(p.id)}
                onDragEnd={() => {
                  setDragId(null);
                  setDropTargetId(null);
                }}
                className={`border-b border-white/[0.05] transition-colors last:border-0 hover:bg-white/[0.02] ${
                  dragId === p.id ? "opacity-40" : ""
                } ${dropTargetId === p.id && dragId !== p.id ? "bg-white/[0.05]" : ""}`}
                data-testid={`portfolio-row-${p.slug}`}
              >
                <td className="px-3 py-4">
                  {canReorder && (
                    <GripVertical
                      className="h-4 w-4 cursor-grab text-neutral-600 transition-colors hover:text-white active:cursor-grabbing"
                      data-testid={`reorder-handle-${p.slug}`}
                    />
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-4">
                    {p.cover_media?.url && (
                      <img src={mediaUrl(p.cover_media.url)} alt="" className="h-11 w-16 rounded-lg border border-white/10 object-cover" />
                    )}
                    <div>
                      <p className="font-semibold text-white">{p.title}</p>
                      <p className="text-xs text-neutral-500">{p.subtitle}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-neutral-400">{p.categories?.length ? p.categories.join(" · ") : p.category}</td>
                <td className="px-5 py-4"><StatusBadge status={p.status} /></td>
                <td className="px-5 py-4 font-mono text-xs text-neutral-500">{(p.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/admin/portfolio/${p.id}/edit`}
                      data-testid={`edit-project-${p.slug}`}
                      className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-white"
                      aria-label={`Edit ${p.title}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => toggleStatus(p)}
                      data-testid={`toggle-status-${p.slug}`}
                      className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-white"
                      aria-label={p.status === "published" ? "Unpublish" : "Publish"}
                    >
                      {p.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(p)}
                      data-testid={`delete-project-${p.slug}`}
                      className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-red-300"
                      aria-label={`Delete ${p.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal target={deleteTarget} noun="project" onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
    </div>
  );
}
